import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Form } from "react-bootstrap";
import { Button } from "../Button/Button";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useQuery } from "Hooks/useQuery";
import { useMutation } from "Hooks/useMutation";
import { getUsuarioPerfil } from "Services/usuario_service";
import {
  getDepartamentos,
  getMunicipios,
  getSucursales,
  createReferido,
} from "Services/referidos_service";
import { Input } from "Components/Input/Input";
import { Select } from "Components/Select/Select";
import { InformativeText } from "Components/InformativeText/InformativeText";
import "./CaptacionLeads.scss";

const referidoSchema = Yup.object({
  telefono: Yup.string()
    .matches(/^\d{8}$/, "El teléfono debe tener exactamente 8 dígitos")
    .required("El teléfono es requerido"),
  idDepartamento: Yup.string().required("El departamento es requerido"),
  idMunicipio: Yup.string().required("El municipio es requerido"),
  idSucursal: Yup.string().required("La sucursal es requerida"),
});

export const CaptacionLeads = ({ open, onClose }) => {
  const { authUser } = useSelector((state) => state.auth);
  const isAuthenticated = Boolean(authUser.token);
  const idUsuario = authUser.idUsuario;

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const { data: perfilData, isLoading: perfilLoading } = useQuery(
    getUsuarioPerfil,
    { idUsuario },
    isAuthenticated
  );

  const { isLoading: isCreating, mutate: crearReferido } =
    useMutation(createReferido);

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const formik = useFormik({
    initialValues: {
      dpi: perfilData?.dpi || "",
      nombres: perfilData?.nombres || "",
      apellidos: perfilData?.apellidos || "",
      telefono: perfilData?.telefono || "",
      idDepartamento: "",
      idMunicipio: "",
      idSucursal: "",
    },
    validationSchema: referidoSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const response = await crearReferido(values);

      if (!Boolean(response?.result)) {
        const errorMessage = response?.message || "Error al crear el referido";
        showSnackbar(errorMessage, "error");
        return;
      }

      showSnackbar("Referido creado exitosamente", "success");
      setTimeout(() => {
        handleClose();
      }, 2000);
    },
  });

  const { data: departamentosData, isLoading: departamentosLoading } = useQuery(
    getDepartamentos,
    {},
    isAuthenticated
  );

  const { data: municipiosData, isLoading: municipiosLoading } = useQuery(
    getMunicipios,
    { idDepartamento: formik.values.idDepartamento },
    isAuthenticated && !!formik.values.idDepartamento
  );

  const { data: sucursalesData, isLoading: sucursalesLoading } = useQuery(
    getSucursales,
    {
      idDepartamento: formik.values.idDepartamento,
      idMunicipio: formik.values.idMunicipio,
    },
    isAuthenticated &&
      !!formik.values.idDepartamento &&
      !!formik.values.idMunicipio
  );

  const handleTelefonoChange = (e) => {
    const numbersOnly = e.target.value.replace(/\D/g, "");
    formik.setFieldValue("telefono", numbersOnly);
  };

  const handleDepartamentoChange = (e) => {
    formik.setValues({
      ...formik.values,
      idDepartamento: e.target.value,
      idMunicipio: "",
      idSucursal: "",
    });
    formik.setTouched({}, false);
  };

  const handleMunicipioChange = (e) => {
    formik.setValues({
      ...formik.values,
      idMunicipio: e.target.value,
      idSucursal: "",
    });
    formik.setTouched({}, false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={handleClose}
      className="captacion-leads-dialog"
    >
      <DialogTitle className="captacion-leads-dialog__title">
        Ingresa tus datos
      </DialogTitle>
      <DialogContent>
        <Form
          onSubmit={formik.handleSubmit}
          noValidate
          className="captacion-leads-form"
        >
          {!perfilData?.telefono && (
            <Input
              label="Número de teléfono"
              name="telefono"
              id="telefono"
              type="tel"
              placeholder="5523 0022"
              value={formik.values.telefono}
              onChange={handleTelefonoChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.telefono && !!formik.errors.telefono}
              errorMessage={
                formik.touched.telefono ? formik.errors.telefono : ""
              }
              maxLength={8}
              autoComplete="tel"
              isLoading={perfilLoading}
            />
          )}

          <Select
            label="Departamento"
            name="idDepartamento"
            id="idDepartamento"
            value={formik.values.idDepartamento}
            onChange={handleDepartamentoChange}
            onBlur={formik.handleBlur}
            isInvalid={
              formik.touched.idDepartamento && !!formik.errors.idDepartamento
            }
            errorMessage={
              formik.touched.idDepartamento ? formik.errors.idDepartamento : ""
            }
            isLoading={departamentosLoading}
          >
            <option value="">Seleccione un departamento</option>
            {departamentosData?.map((departamento) => (
              <option
                key={departamento.idDepartamento}
                value={departamento.idDepartamento}
              >
                {departamento.nombre}
              </option>
            ))}
          </Select>

          {formik.values.idDepartamento && (
            <Select
              label="Municipio"
              name="idMunicipio"
              id="idMunicipio"
              value={formik.values.idMunicipio}
              onChange={handleMunicipioChange}
              onBlur={formik.handleBlur}
              isInvalid={
                formik.touched.idMunicipio && !!formik.errors.idMunicipio
              }
              errorMessage={
                formik.touched.idMunicipio ? formik.errors.idMunicipio : ""
              }
              isLoading={municipiosLoading}
            >
              <option value="">Seleccione un municipio</option>
              {municipiosData?.map((municipio) => (
                <option
                  key={municipio.idMunicipio}
                  value={municipio.idMunicipio}
                >
                  {municipio.nombre}
                </option>
              ))}
            </Select>
          )}

          {formik.values.idDepartamento && formik.values.idMunicipio && (
            <>
              <Select
                label="Sucursal"
                name="idSucursal"
                id="idSucursal"
                value={formik.values.idSucursal}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={
                  formik.touched.idSucursal && !!formik.errors.idSucursal
                }
                errorMessage={
                  formik.touched.idSucursal ? formik.errors.idSucursal : ""
                }
                isLoading={sucursalesLoading}
              >
                <option value="">Seleccione una sucursal</option>
                {sucursalesData?.map((sucursal) => (
                  <option key={sucursal.idSucursal} value={sucursal.idSucursal}>
                    {sucursal.nombre}
                  </option>
                ))}
              </Select>

              {!sucursalesLoading && !sucursalesData && (
                <InformativeText>
                  Aún no contamos con sucursales en su municipio. Seleccione
                  otro municipio para continuar.
                </InformativeText>
              )}
            </>
          )}
        </Form>
      </DialogContent>
      <DialogActions>
        <div className="captacion-leads-dialog__actions">
          <Button onClick={handleClose} size="medium" variant="secundario">
            Cerrar
          </Button>
          <Button
            onClick={formik.handleSubmit}
            autoFocus
            size="medium"
            disabled={
              perfilLoading || isCreating || !formik.isValid || !formik.dirty
            }
          >
            {isCreating && (
              <CircularProgress size={20} sx={{ marginRight: 1 }} />
            )}
            Enviar
          </Button>
        </div>
      </DialogActions>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
